import { useState, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { X, Search, MessageSquare, Hash, Check } from "lucide-react";
import { listarUsuariosAtivos } from "../../services/usuarios";
import { abrirOuCriarDM, criarGrupo } from "../../services/chat";
import { useAuth } from "../../hooks/useAuth";
import { useEscape } from "../../hooks/useEscape";

export function NovaConversaModal({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const podeGrupo = user?.perfil === "gestor" || user?.perfil === "admin";
  const [modo, setModo] = useState<"dm" | "grupo">("dm");
  const [busca, setBusca] = useState("");
  const [selecionados, setSelecionados] = useState<string[]>([]);
  const [nomeGrupo, setNomeGrupo] = useState("");
  useEscape(true, onClose);

  const { data: usuarios = [] } = useQuery({ queryKey: ["usuarios-ativos"], queryFn: listarUsuariosAtivos });

  const filtrados = useMemo(() => {
    const t = busca.trim().toLowerCase();
    return usuarios
      .filter((u) => u.id !== user?.id && u.perfil !== "cliente")
      .filter((u) => !t || u.nome.toLowerCase().includes(t) || u.email.toLowerCase().includes(t));
  }, [usuarios, busca, user?.id]);

  const dmM = useMutation({
    mutationFn: (id: string) => abrirOuCriarDM(id),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ["chat-conversas"] });
      onClose();
      navigate(`/chat/${c.id}`);
    },
  });

  const grupoM = useMutation({
    mutationFn: () => criarGrupo(nomeGrupo.trim(), selecionados),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ["chat-conversas"] });
      onClose();
      navigate(`/chat/${c.id}`);
    },
  });

  function toggle(id: string) {
    setSelecionados((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md max-h-[88vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">Nova conversa</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><X size={18} /></button>
        </div>

        <div className="px-4 pt-3">
          <div className="flex bg-slate-100 rounded-lg p-0.5 mb-3">
            <button onClick={() => setModo("dm")} className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium ${modo === "dm" ? "bg-white text-blue-600 shadow-sm" : "text-slate-500"}`}>
              <MessageSquare size={12} /> Direta
            </button>
            {podeGrupo && (
              <button onClick={() => setModo("grupo")} className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium ${modo === "grupo" ? "bg-white text-blue-600 shadow-sm" : "text-slate-500"}`}>
                <Hash size={12} /> Grupo
              </button>
            )}
          </div>

          {modo === "grupo" && (
            <input
              type="text"
              value={nomeGrupo}
              onChange={(e) => setNomeGrupo(e.target.value)}
              placeholder="Nome do grupo"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500 mb-2"
            />
          )}

          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar pessoa…"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {filtrados.length === 0 && <p className="text-sm text-slate-400 text-center py-8">Nenhuma pessoa</p>}
          {filtrados.map((u) => {
            const sel = selecionados.includes(u.id);
            return (
              <button
                key={u.id}
                onClick={() => modo === "dm" ? dmM.mutate(u.id) : toggle(u.id)}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 rounded-lg flex items-center gap-3"
              >
                <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold shrink-0">
                  {u.nome.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{u.nome}</p>
                  <p className="text-[11px] text-slate-500 truncate">{u.email} · {u.perfil}</p>
                </div>
                {modo === "grupo" && (
                  <div className={`w-5 h-5 rounded border ${sel ? "bg-blue-600 border-blue-600 text-white" : "border-slate-300"} flex items-center justify-center shrink-0`}>
                    {sel && <Check size={12} />}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {modo === "grupo" && (
          <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2">
            <button onClick={onClose} className="px-3 py-1.5 rounded text-sm bg-slate-100 hover:bg-slate-200">Cancelar</button>
            <button
              onClick={() => grupoM.mutate()}
              disabled={!nomeGrupo.trim() || selecionados.length === 0 || grupoM.isPending}
              className="px-4 py-1.5 rounded text-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
            >
              {grupoM.isPending ? "Criando…" : `Criar grupo (${selecionados.length})`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
